import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Popper,
  FormControl,
  Select,
  FormControlLabel,
  InputLabel,
  TextField,
  Typography,
  PaginationItem,
} from '@mui/material';
import MuiPagination from '@mui/material/Pagination';
import { styled } from '@mui/material/styles';
import {
  DataGridPro,
  gridPageCountSelector,
  GridPagination,
  useGridSelector,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  getUsersData,
  getUsersFilters,
  postImportData,
} from '../../../../packages/common/api';
import Page from '../../../../packages/common/shared-ui/src/components/Page';
import ChevronIcon from '../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import ClearIcon from '../../../../packages/common/shared-ui/src/icons/ClearIcon';
import PaginationNextButtonIcon from '../../../../packages/common/shared-ui/src/icons/PaginationNextButtonIcon';
import PaginationPreviousButtonIcon from '../../../../packages/common/shared-ui/src/icons/PaginationPreviousButtonIcon';
import SortIcon from '../../../../packages/common/shared-ui/src/icons/SortIcon';
import SubmitIcon from '../../../../packages/common/shared-ui/src/icons/SubmitIcon';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';
import { getTranslatedText } from '../../../../packages/common/utils/getTranslatedText';

// Original class: blockWrapper
const StyledBlockWrapper = styled(Box)(theme => ({
  width: '100%',
  padding: '24px 32px',
  borderRadius: '16px',
  backgroundColor: '#fff',
}));
// Original class: filterSelect
const StyledFilterSelect = styled(Select)(({ theme }) => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    backgroundColor: '#FFFFFF',
    width: '100%',
    fontSize: '14px',
    lineHeight: '20px',
    border: '1px solid transparent',
    '&:hover': {
      border: '1px solid transparent',
      backgroundColor: '#FFFFFF',
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      padding: 0,
      border: 'none',
    },
    '& .MuiAutocomplete-endAdornment': {
      '& .MuiButtonBase-root.MuiIconButton-root.MuiAutocomplete-clearIndicator':
        {
          '& .MuiSvgIcon-root': {
            width: '20px',
            height: '20px',
          },
        },
      '& .MuiSvgIcon-root': {
        width: '24px',
        height: '24px',
        position: 'static',
      },
    },
    '& .MuiAutocomplete-input': {
      fontSize: '14px',
      lineHeight: '20px',
      color: '#7174AC',
      padding: '0',
    },
  },

  '&.MuiAutocomplete-hasClearIcon': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderColor: '#3448FF',
      '&:hover': {
        borderColor: '#3448FF',
      },
    },
  },

  '& .MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC',
    fontSize: '14px',
    lineHeight: '20px',
    top: '-5px',
    zIndex: 99,
    '&.MuiFormLabel-filled': {
      display: 'none',
    },
  },

  '&.Mui-expanded': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderRadius: '16px 16px 0 0',
      '& .MuiOutlinedInput-notchedOutline': {
        padding: 0,
      },
    },
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '&.Mui-focused': {
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '& + .MuiAutocomplete-popper': {
    minWidth: '180px!important',
    width: 'auto!important',
    maxWidth: '300px!important',
    '& .MuiPaper-root.MuiAutocomplete-paper': {
      borderRadius: '0 0 16px 16px',
      borderTop: '1px solid #E4E4EF',
      boxShadow: '0px 5px 10px -8px #A0A1AA66',
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        '& li': {
          whiteSpace: ' nowrap',
          fontSize: '12px',
          lineHeight: '20px',
          color: '#212346',
          '&:hover': {
            backgroundColor: '#DCDFF4',
          },
        },
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#7174AC',
          borderRadius: '5px',
        },
      },
    },
  },
}));
const ImportNewData = () => {
  const getLocalString = useTranslation();
  const pageTitle = getLocalString('AdminImport', 'title');
  const doneMessage = getLocalString('AdminImport', 'labels', 'loadingDone');
  const loadingImportErrors = getLocalString(
    'AdminImport',
    'labels',
    'loadingError',
  );
  const [dataType, setDataType] = useState('');
  const [fileData, setFileData] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [locked, setLocked] = useState(false);
  const [dataTypeOptions, setDataTypeOptions] = useState([
    { value: 'timesheet' },
    { value: 'issue' },
    { value: 'user' },
    { value: 'project' },
    { value: 'rates' },
  ]);
  const [loading, setLoading] = useState(false);
  const handleStartImport = useCallback(
    async event => {
      setLocked(true);
      setSuccess('');
      setError('');
      try {
        const { data: response } = await postImportData(dataType, fileData);
        if (response) {
          if ((response.status = 'ok')) {
            setSuccess(doneMessage);
          }
          if (response.errorRows.length > 0) {
            setError(loadingImportErrors + response.errorRows.join(','));
          }
          setLocked(false);
          setDataType('');
          setFileData('');
        }
      } catch (error) {
        console.error('ERROR WITH SAVE', error);
        setLocked(false);
      }
    },
    [dataType, fileData, loadingImportErrors, doneMessage],
  );
  return (
    <Page title={pageTitle} width='100%'>
      <Grid container spacing={5} sx={{ mb: '30px' }}>
        <Grid>
          <Typography
            variant='h1'
            sx={{
              m: 0,
              padding: '10px 0',
            }}
          >
            {pageTitle}
          </Typography>
        </Grid>
      </Grid>
      <StyledBlockWrapper
        sx={{
          padding: '18px 28px 20px 28px',
          marginTop: '16px',
          position: 'relative',
        }}
      >
        <Grid container flexDirection={'column'}>
          <Grid>
            {success && (
              <Typography
                variant='h5'
                sx={{
                  color: '#37C670',
                }}
              >
                {success}
              </Typography>
            )}
            {error && (
              <Typography
                variant='h5'
                sx={{
                  color: '#E03737',
                }}
              >
                {error}
              </Typography>
            )}
            <FormControl
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '300px',
                marginTop: '16px',
                flex: 1,
              }}
            >
              <Typography
                variant='h5'
                sx={{
                  color: '#41424E',
                }}
              >
                {getLocalString('AdminImport', 'labels', 'dataType')}
              </Typography>
              <StyledFilterSelect
                IconComponent={props => (
                  <ChevronIcon direction={'down'} {...props} />
                )}
                value={dataType ? dataType : ''}
                onChange={event => setDataType(event.target.value)}
              >
                {dataTypeOptions?.map((item, index) => (
                  <MenuItem value={item.value} key={index}>
                    <Typography
                      variant='h5'
                      sx={{
                        color: '#212346',
                      }}
                    >
                      {getLocalString('AdminImport', 'labels', item.value)}
                    </Typography>
                  </MenuItem>
                ))}
              </StyledFilterSelect>
            </FormControl>
          </Grid>
          <Grid
            container
            sx={{
              alignItems: 'center',
              marginTop: '16px',
            }}
          >
            {fileData?.name}
            <Button
              variant='defaultGreyPrimary'
              component='label'
              sx={{ marginLeft: '10px', padding: '6px 16px' }}
            >
              <Typography variant='h5'>
                {getLocalString('labels', 'selectFile')}
              </Typography>
              <input
                hidden
                multiple
                type='file'
                onChange={event => setFileData(event.target.files[0])}
              />
            </Button>
          </Grid>
          <Grid
            container
            sx={{
              alignItems: 'center',
              marginTop: '16px',
            }}
          >
            {dataType && fileData.name && (
              <Button
                variant='defaultGreyPrimary'
                component='label'
                disabled={locked}
                sx={{ marginLeft: '10px', padding: '6px 16px' }}
                onClick={event => handleStartImport()}
              >
                <Typography variant='h5'>
                  {getLocalString('labels', 'loadFile')}
                </Typography>
              </Button>
            )}
            {loading && (
              <Grid
                container
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '30px',
                  height: '30px',
                  marginLeft: '10px',
                }}
              >
                <CircularProgress size={20} color='secondary' />
              </Grid>
            )}
          </Grid>
        </Grid>
      </StyledBlockWrapper>
    </Page>
  );
};

export default ImportNewData;
